import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Select, Space, Typography, Tooltip, Alert, Empty } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../api/client';
import WorkerSparkline from '../components/WorkerSparkline';
import WorkerAnalyticsDetail from '../components/WorkerAnalyticsDetail';
import {
  WorkerTelemetry, SAMPLE_NOTE, NUM_STYLE, availabilityTag, formatDuration, formatKm, formatMs,
} from '../utils/workerTelemetry';

const { Text } = Typography;

// The shapes and formatters moved to utils/workerTelemetry so the workers card
// on Settings can share them; re-exported here because that card (and its
// cells) has always imported them from this page.
export type { WorkerTelemetry, WorkerDaySeries, WorkerCountryShare, WorkerOutage } from '../utils/workerTelemetry';
export { availabilityTag, formatDuration, formatMs, formatKm, SAMPLE_NOTE } from '../utils/workerTelemetry';

// Worker analytics: what each checker actually did, day by day.
//
// The page is built around the four questions it exists to answer — which
// workers are barely used, which are unstable, which are slow, and whether the
// geographic split makes sense — so every column here is one of those, and
// nothing is shown that does not serve one.
//
// Layout budget: the target machine reports a 1280px viewport, which leaves
// ~1030px of content once the sidebar and padding are paid for. Each column is
// one line of the answer; the supporting numbers are one hover (tooltip) or one
// click (expanded row) away, because a column that needs sideways scrolling to
// reach is a column nobody reads.

// The one supporting fact worth a second look on the Check time column, in
// order of how much it changes the reading: time lost to dead targets is not
// this worker's fault, queue wait is not probing, and the p95 says whether the
// average is the whole story.
const timingNote = (w: WorkerTelemetry): { warn: boolean; text: string } | null => {
  if (w.avgLostMs > 0) return { warn: true, text: `incl. ${formatMs(w.avgLostMs)} dead` };
  if (w.avgWaitMs >= 50) return { warn: false, text: `+${formatMs(w.avgWaitMs)} wait` };
  if (w.p95DurationMs > 0) return { warn: false, text: `p95 ${formatMs(w.p95DurationMs)}` };
  return null;
};

// Every measurement cell on this page has the same shape: the value, right
// aligned with tabular digits so the column can be scanned straight down, and
// at most one qualifier in 12px underneath it. One reading order for the whole
// table — the eye learns it once on the first column and reuses it on all six.
const Measure: React.FC<{
  value: React.ReactNode;
  note?: React.ReactNode;
  noteType?: 'secondary' | 'warning';
}> = ({ value, note, noteType = 'secondary' }) => (
  <div style={{ textAlign: 'right', cursor: 'help', ...NUM_STYLE }}>
    <div style={{ fontSize: 13, lineHeight: '18px' }}>{value}</div>
    {note != null && (
      <Text
        type={noteType}
        style={{ fontSize: 12, lineHeight: '16px', display: 'block', ...NUM_STYLE }}
        ellipsis
      >
        {note}
      </Text>
    )}
  </div>
);

const WorkerAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<WorkerTelemetry[]>([]);
  const [days, setDays] = useState(14);
  const [retention, setRetention] = useState(60);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await apiClient.get(`/admin/workers/telemetry?days=${days}`);
      setData(res.data?.workers || []);
      setRetention(res.data?.retention || 60);
      setError(null);
    } catch (e: any) {
      // Clearing rather than leaving the previous window's rows on screen: the
      // control above them already says a different window, and totals read
      // under the wrong label are worse than no totals.
      setData([]);
      setError(e.response?.data?.error || 'Could not load worker telemetry');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totalChecks = data.reduce((sum, w) => sum + w.checks, 0);

  // One y axis for every sparkline in the table: the busiest single day any
  // worker had in this window. Per-row normalisation made an idle worker and
  // the fleet's workhorse draw the same picture.
  const fleetDayMax = useMemo(
    () => Math.max(1, ...data.flatMap((w) => w.days.map((d) => d.checks))),
    [data],
  );

  const columns: ColumnsType<WorkerTelemetry> = [
    {
      title: 'Worker',
      dataIndex: 'workerName',
      key: 'name',
      // No fixed width: the name column takes the slack, so Targets sits
      // beside the other measures instead of past a 300px empty gap.
      ellipsis: { showTitle: false },
      render: (name: string) => (
        // Never wrapped: "stand-multiport" broken across two lines is both
        // unreadable and twice the row height.
        <Tooltip title={name}><Text strong style={{ fontSize: 13 }}>{name}</Text></Tooltip>
      ),
    },
    {
      title: 'Checks',
      key: 'load',
      width: 104,
      align: 'right',
      sorter: (a, b) => a.checks - b.checks,
      defaultSortOrder: 'descend',
      render: (_, w) => (
        <Tooltip title={`${w.checksProxy.toLocaleString()} proxy · ${w.checksIp.toLocaleString()} ip · ${w.sharePercent}% of all checks in this window`}>
          {/* The share meter that used to sit here drew the same magnitude the
              number and the sparkline beside it already draw — three encodings
              of one fact across 135px. The share stays as the qualifier. */}
          <Measure value={w.checks.toLocaleString()} note={`${w.sharePercent}% of all`} />
        </Tooltip>
      ),
    },
    {
      title: (
        <Tooltip title="Checks per day. Every worker is drawn against the same scale — the fleet's busiest day — so bar heights compare across rows.">
          <span>Checks/day</span>
        </Tooltip>
      ),
      key: 'series',
      width: 140,
      render: (_, w) => <WorkerSparkline days={w.days} width={124} height={28} max={fleetDayMax} />,
    },
    {
      title: <Tooltip title={SAMPLE_NOTE}><span>Uptime</span></Tooltip>,
      key: 'availability',
      width: 96,
      align: 'right',
      sorter: (a, b) => a.availabilityPercent - b.availabilityPercent,
      render: (_, w) => (
        <Tooltip title={
          <div style={{ fontSize: 12 }}>
            <div>{SAMPLE_NOTE}</div>
            {w.disconnects > 0 && (
              <div>
                {w.disconnects} drop{w.disconnects === 1 ? '' : 's'} · longest{' '}
                {formatDuration(w.longestOutageSec)} · {formatDuration(w.totalOutageSec)} offline in total
              </div>
            )}
          </div>
        }>
          <Measure
            value={availabilityTag(w.availabilityPercent)}
            note={w.disconnects > 0
              ? `${w.disconnects} drop${w.disconnects === 1 ? '' : 's'}`
              : undefined}
            noteType="warning"
          />
        </Tooltip>
      ),
    },
    {
      title: 'Check time',
      key: 'duration',
      width: 116,
      align: 'right',
      sorter: (a, b) => a.avgDurationMsProxy - b.avgDurationMsProxy,
      render: (_, w) => {
        // Probe time, not wall clock: a check that waited 8 seconds for a free
        // slot and then took 400 ms is a busy worker, not a slow one, and the
        // two were indistinguishable while the wait sat inside the number.
        const note = timingNote(w);
        return (
          <Tooltip overlayStyle={{ maxWidth: 360 }} title={
            <div style={{ fontSize: 12 }}>
              <div>probing {Math.round(w.avgProbeMs)} ms avg — wall clock proxy {Math.round(w.avgDurationMsProxy)} ms · ip {Math.round(w.avgDurationMsIp)} ms</div>
              <div>waiting for a slot: {Math.round(w.avgWaitMs)} ms avg, {Math.round(w.maxWaitMs)} ms worst</div>
              <div>p50 {Math.round(w.p50DurationMs)} · p95 {Math.round(w.p95DurationMs)} ms, {w.measuredChecks.toLocaleString()} checks timed, worst {Math.round(w.maxDurationMs)} ms</div>
              <div>
                of the proxy time above, {Math.round(w.avgLostMs || 0)} ms per proxy check went on targets
                that did not answer before another one did — that part belongs to the target, not to this
                worker: a dead URL at the head of the cascade adds its timeout to every check in the fleet
              </div>
            </div>
          }>
            <Measure
              value={formatMs(w.avgProbeMs)}
              note={note?.text}
              noteType={note?.warn ? 'warning' : 'secondary'}
            />
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="Share of this worker's own slots in use, sampled every 30s alongside uptime"><span>Slots</span></Tooltip>,
      key: 'utilisation',
      width: 86,
      align: 'right',
      sorter: (a, b) => a.utilisationPercent - b.utilisationPercent,
      render: (_, w) => (
        <Tooltip title={
          <div style={{ fontSize: 12 }}>
            <div>{w.utilisationPercent}% of its slots in use on average</div>
            <div>no free slot {w.saturatedPercent}% of the time</div>
            <div>queue avg {Math.round(w.avgQueue)} · peak {w.maxQueue}</div>
          </div>
        }>
          <Measure
            value={`${w.utilisationPercent}%`}
            note={w.saturatedPercent > 0 ? `${w.saturatedPercent}% full` : undefined}
            noteType="warning"
          />
        </Tooltip>
      ),
    },
    {
      title: <Tooltip title="How often this worker's verdict matched the one the previous check of the same target produced from a different worker">
        <span>Agreed</span></Tooltip>,
      key: 'agreement',
      width: 96,
      align: 'right',
      sorter: (a, b) => a.agreementPercent - b.agreementPercent,
      render: (_, w) => {
        // The answer to "is it the proxy or the worker". Failures everyone sees
        // belong to the target; failures only this worker sees belong to this
        // worker's view of the network.
        if (w.agreementPercent < 0) {
          return (
            <Tooltip title="No target this worker checked was also checked by another worker in this window">
              <Measure value={<Text type="secondary" style={{ fontSize: 12 }}>no 2nd opinion</Text>} />
            </Tooltip>
          );
        }
        // Colour only when it is a finding. A green tag on every healthy row
        // spends the status channel on "nothing to see here" and makes the one
        // worker that disagrees with the fleet harder, not easier, to spot —
        // the Uptime tag beside it is already carrying the row's status colour.
        const type = w.agreementPercent >= 97 ? undefined
          : w.agreementPercent >= 90 ? 'warning' as const
          : 'danger' as const;
        return (
          <Tooltip title={
            <div style={{ fontSize: 12 }}>
              <div>{w.agreementPercent}% of {w.secondOpinions.toLocaleString()} checks compared</div>
              <div>{w.onlyThisWorkerFail.toLocaleString()} failures only it saw · {w.onlyOtherFail.toLocaleString()} only others saw</div>
            </div>
          }>
            <Measure
              value={<Text type={type} style={{ fontSize: 13, ...NUM_STYLE }}>{w.agreementPercent}%</Text>}
              note={`of ${w.secondOpinions.toLocaleString()}`}
            />
          </Tooltip>
        );
      },
    },
    {
      title: <Tooltip title="How far the checked targets were, and which countries they were in"><span>Targets</span></Tooltip>,
      key: 'geo',
      width: 148,
      align: 'right',
      sorter: (a, b) => a.avgDistanceKm - b.avgDistanceKm,
      render: (_, w) => {
        // Distance is measured against where the target's server is — the same
        // coordinates the dispatcher chose this worker with. A server whose
        // location is not known yet cannot be measured at all, so the coverage
        // is in the tooltip rather than implied by the number.
        const countries = w.countries || [];
        return (
          <Tooltip overlayStyle={{ maxWidth: 360 }} title={
            <div style={{ fontSize: 12 }}>
              <div>{formatKm(w.avgDistanceKm)} avg · {formatKm(w.maxDistanceKm)} max to the target's server</div>
              <div>{w.geoOptimalPercent}% went to the nearest worker, of {w.geoEvaluatedChecks.toLocaleString()} measurable checks</div>
              <div>
                {countries.length
                  ? `${countries.map((c) => `${c.country} ${c.percent}% (${c.checks.toLocaleString()})`).join(' · ')} — of ${w.countryKnownChecks.toLocaleString()} checks with a known country`
                  : 'no target country known in this window'}
              </div>
            </div>
          }>
            <Measure
              value={formatKm(w.avgDistanceKm)}
              note={countries.length > 0
                ? `${countries.slice(0, 3).map((c) => `${c.country} ${c.percent}%`).join(' · ')}${countries.length > 3 ? ` +${countries.length - 3}` : ''}`
                : undefined}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <Typography.Title level={4} style={{ margin: 0 }}>Worker Analytics</Typography.Title>
          <Space size={8}>
            {totalChecks > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {totalChecks.toLocaleString()} checks · kept {retention} days
              </Text>
            )}
            <Select
              value={days}
              onChange={setDays}
              size="small"
              style={{ width: 132 }}
              options={[
                { value: 1, label: 'Today' },
                { value: 7, label: 'Last 7 days' },
                { value: 14, label: 'Last 14 days' },
                { value: 30, label: 'Last 30 days' },
                { value: 60, label: `Last ${retention} days` },
              ]}
            />
          </Space>
        </Space>
        {/* One line, not three: every pixel above the first row is paid for out
            of the 728px the operator can actually see. */}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Which checkers are barely used, unstable, slow, or far from their targets.
          Hover any cell for detail; expand a row for the day-by-day history.
        </Text>
      </div>

      {error && <Alert type="error" showIcon style={{ marginBottom: 12 }} message={error} />}

      <Card styles={{ body: { padding: 12 } }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey={(w) => w.workerId || w.workerName}
          size="small"
          tableLayout="fixed"
          loading={loading}
          pagination={false}
          locale={{ emptyText: <Empty description="No telemetry yet — it is collected as checks run" /> }}
          expandable={{ expandedRowRender: (w) => <WorkerAnalyticsDetail worker={w} /> }}
        />
      </Card>
    </div>
  );
};

export default WorkerAnalyticsPage;
